import { defaultSecurity } from "./security-defaults.js";

export interface TopologyTemplate {
  id: string;
  name: string;
  type: string;
  description: string;
  agentCount: number;
  definition: any;
}

function identity(persona: string, instructions: string) {
  return { persona, instructions };
}

const RAW_TEMPLATES: TopologyTemplate[] = [
  {
    id: "pipeline",
    name: "Pipeline",
    type: "pipeline",
    description: "Sequential processing chain where each agent passes output to the next",
    agentCount: 3,
    definition: {
      type: "pipeline",
      agents: [
        { id: "analyzer", name: "Analyzer", backend: "claude-agent-sdk", identity: identity("You are an analyst.", "Analyze the input"), capabilities: ["analysis"] },
        { id: "processor", name: "Processor", backend: "claude-agent-sdk", identity: identity("You are a processor.", "Process the analysis"), capabilities: ["processing"] },
        { id: "reporter", name: "Reporter", backend: "claude-agent-sdk", identity: identity("You are a reporter.", "Generate a report"), capabilities: ["reporting"] },
      ],
      edges: [
        { from: "analyzer", to: "processor", type: "pipes-to", bidirectional: false },
        { from: "processor", to: "reporter", type: "pipes-to", bidirectional: false },
      ],
      entryPoint: "analyzer",
      exitPoints: ["reporter"],
    },
  },
  {
    id: "hierarchical",
    name: "Hierarchical",
    type: "hierarchical",
    description: "Manager delegates to specialist workers and aggregates results",
    agentCount: 4,
    definition: {
      type: "hierarchical",
      agents: [
        { id: "manager", name: "Manager", backend: "claude-agent-sdk", identity: identity("You are a project manager.", "Coordinate workers"), capabilities: ["coordination"] },
        { id: "worker-1", name: "Research", backend: "claude-agent-sdk", identity: identity("You are a researcher.", "Conduct research"), capabilities: ["research"] },
        { id: "worker-2", name: "Analysis", backend: "openai-agents", identity: identity("You are an analyst.", "Analyze data"), capabilities: ["analysis"] },
        { id: "worker-3", name: "Writer", backend: "claude-agent-sdk", identity: identity("You are a writer.", "Write content"), capabilities: ["writing"] },
      ],
      edges: [
        { from: "manager", to: "worker-1", type: "delegates-to", bidirectional: false },
        { from: "manager", to: "worker-2", type: "delegates-to", bidirectional: false },
        { from: "manager", to: "worker-3", type: "delegates-to", bidirectional: false },
        { from: "worker-1", to: "manager", type: "reports-to", bidirectional: false },
        { from: "worker-2", to: "manager", type: "reports-to", bidirectional: false },
        { from: "worker-3", to: "manager", type: "reports-to", bidirectional: false },
      ],
      entryPoint: "manager",
      exitPoints: ["manager"],
    },
  },
  {
    id: "star",
    name: "Star",
    type: "star",
    description: "Central hub connects to all peripheral agents",
    agentCount: 5,
    definition: {
      type: "star",
      agents: [
        { id: "hub", name: "Hub", backend: "claude-agent-sdk", identity: identity("You are a central coordinator.", "Coordinate all nodes"), capabilities: ["coordination"] },
        { id: "node-1", name: "Node A", backend: "claude-agent-sdk", identity: identity("You are Specialist A.", "Handle task A"), capabilities: ["task-a"] },
        { id: "node-2", name: "Node B", backend: "openai-agents", identity: identity("You are Specialist B.", "Handle task B"), capabilities: ["task-b"] },
        { id: "node-3", name: "Node C", backend: "claude-agent-sdk", identity: identity("You are Specialist C.", "Handle task C"), capabilities: ["task-c"] },
        { id: "node-4", name: "Node D", backend: "cloudflare-agents", identity: identity("You are Specialist D.", "Handle task D"), capabilities: ["task-d"] },
      ],
      edges: [
        { from: "hub", to: "node-1", type: "delegates-to", bidirectional: true },
        { from: "hub", to: "node-2", type: "delegates-to", bidirectional: true },
        { from: "hub", to: "node-3", type: "delegates-to", bidirectional: true },
        { from: "hub", to: "node-4", type: "delegates-to", bidirectional: true },
      ],
      entryPoint: "hub",
      exitPoints: ["hub"],
    },
  },
  {
    id: "mesh",
    name: "Mesh",
    type: "mesh",
    description: "All agents can communicate with each other freely",
    agentCount: 3,
    definition: {
      type: "mesh",
      agents: [
        { id: "agent-1", name: "Agent Alpha", backend: "claude-agent-sdk", identity: identity("You are Agent Alpha.", "Collaborate freely"), capabilities: ["general"] },
        { id: "agent-2", name: "Agent Beta", backend: "openai-agents", identity: identity("You are Agent Beta.", "Collaborate freely"), capabilities: ["general"] },
        { id: "agent-3", name: "Agent Gamma", backend: "claude-agent-sdk", identity: identity("You are Agent Gamma.", "Collaborate freely"), capabilities: ["general"] },
      ],
      edges: [
        { from: "agent-1", to: "agent-2", type: "peers-with", bidirectional: true },
        { from: "agent-2", to: "agent-3", type: "peers-with", bidirectional: true },
        { from: "agent-1", to: "agent-3", type: "peers-with", bidirectional: true },
      ],
      entryPoint: "agent-1",
      exitPoints: ["agent-1"],
    },
  },
  {
    id: "debate",
    name: "Debate",
    type: "custom",
    description: "Two agents debate while a judge evaluates and decides",
    agentCount: 3,
    definition: {
      type: "custom",
      agents: [
        { id: "proponent", name: "Proponent", backend: "claude-agent-sdk", identity: identity("You are the proponent in a debate.", "Argue in favor"), capabilities: ["argumentation"] },
        { id: "opponent", name: "Opponent", backend: "openai-agents", identity: identity("You are the opponent in a debate.", "Argue against"), capabilities: ["argumentation"] },
        { id: "judge", name: "Judge", backend: "claude-agent-sdk", identity: identity("You are an impartial judge.", "Evaluate arguments and decide"), capabilities: ["evaluation", "decision-making"] },
      ],
      edges: [
        { from: "proponent", to: "judge", type: "reports-to", bidirectional: false },
        { from: "opponent", to: "judge", type: "reports-to", bidirectional: false },
        { from: "proponent", to: "opponent", type: "peers-with", bidirectional: true },
      ],
      entryPoint: "judge",
      exitPoints: ["judge"],
    },
  },
  {
    id: "map-reduce",
    name: "Map-Reduce",
    type: "custom",
    description: "Distribute work across mappers then combine results in reducer",
    agentCount: 4,
    definition: {
      type: "custom",
      agents: [
        { id: "splitter", name: "Splitter", backend: "claude-agent-sdk", identity: identity("You are a task splitter.", "Split input into chunks"), capabilities: ["splitting"] },
        { id: "mapper-1", name: "Mapper 1", backend: "claude-agent-sdk", identity: identity("You are a data processor.", "Process chunk"), capabilities: ["mapping"] },
        { id: "mapper-2", name: "Mapper 2", backend: "openai-agents", identity: identity("You are a data processor.", "Process chunk"), capabilities: ["mapping"] },
        { id: "reducer", name: "Reducer", backend: "claude-agent-sdk", identity: identity("You are a result synthesizer.", "Combine results"), capabilities: ["reducing", "synthesis"] },
      ],
      edges: [
        { from: "splitter", to: "mapper-1", type: "pipes-to", bidirectional: false },
        { from: "splitter", to: "mapper-2", type: "pipes-to", bidirectional: false },
        { from: "mapper-1", to: "reducer", type: "pipes-to", bidirectional: false },
        { from: "mapper-2", to: "reducer", type: "pipes-to", bidirectional: false },
      ],
      entryPoint: "splitter",
      exitPoints: ["reducer"],
    },
  },
];

/**
 * Every agent in a topology definition requires a security profile (core's
 * `validateTopology` enforces this), so fill in sensible defaults the raw
 * templates omit: the entry agent is trusted, the rest semi-trusted.
 */
export const TOPOLOGY_TEMPLATES: TopologyTemplate[] = RAW_TEMPLATES.map((t) => ({
  ...t,
  definition: {
    ...t.definition,
    agents: t.definition.agents.map((a: any) => ({
      ...a,
      description: a.description ?? `${a.name} agent`,
      capabilities: a.capabilities ?? [],
      security:
        a.security ??
        defaultSecurity(a.id === t.definition.entryPoint ? "trusted" : "semi-trusted"),
    })),
  },
}));
